import { jwtDecode } from "jwt-decode";
import { routes } from "../apiroutes";
import HttpService from "../httpServices";
// import useFetchItem from "./useFetchItem";
import useMutateItem from "../useMutateItem";
import { getAccessToken, stringifyData } from "../../utils";
import { ErrorHandler } from "../errorHandler";
import { showErrorAlert, showSuccessAlert } from "../../utils/alert";

const httpService = new HttpService();

/**
 * Extract payroll roles and permissions from JWT token
 * @param {string} token - JWT token string
 * @returns {Object} - Object with payroll_roles and payroll_permissions arrays
 */
export const extractPayrollDataFromToken = (token) => {
  try {
    // Return empty arrays if no token
    if (!token) return { payroll_roles: [], payroll_permissions: [] };
    
    // Check if token is a string - if it's an object, return early
    if (typeof token !== 'string') {
      return { payroll_roles: [], payroll_permissions: [] };
    }
    
    // Remove "Bearer " prefix if present
    const cleanToken = token.replace(/^Bearer\s+/i, '');
    
    // Additional check: ensure cleanToken is not empty
    if (!cleanToken || cleanToken.trim().length === 0) {
      return { payroll_roles: [], payroll_permissions: [] };
    }
    
    const decoded = jwtDecode(cleanToken);
    
    return {
      payroll_roles: decoded.payroll_roles || [],
      payroll_permissions: decoded.payroll_permissions || []
    };
  } catch (err) {
    // Silently return empty arrays - don't log errors for missing/invalid tokens
    // This is expected when user hasn't logged in yet
    return { payroll_roles: [], payroll_permissions: [] };
  }
};

export const getToken = () => {
    const token = getAccessToken();
    const AuthStr = "Bearer ".concat(token);
    return token ? AuthStr : null;
  };
  

export const useLogin = (handleSuccess, handleError) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) =>
      httpService.postDataWithoutToken(payload, routes.oauthtoken()),
    onSuccess: (requestParams) => {
      const resData = requestParams?.data || {};
      
      // Store auth tokens
      sessionStorage.setItem("auth_data", stringifyData(resData));
      sessionStorage.setItem("auth_token", stringifyData(resData?.access_token));
      sessionStorage.setItem("auth_token_refresh", resData?.refresh_token);
      sessionStorage.setItem("firstTimeLogin", resData?.firstTimeLogin);
      
      // Extract payroll roles/permissions from token (primary source)
      const tokenPayrollData = extractPayrollDataFromToken(resData?.access_token);
      
      // Handle new enhanced response format with user object
      if (resData?.user) {
        // Store user role from new response format
        if (resData.user.role) {
          localStorage.setItem('userRole', resData.user.role);
        }
        
        // Store user branch info if available
        if (resData.user.branch_id) {
          localStorage.setItem('userBranch', JSON.stringify({
            id: resData.user.branch_id,
            name: resData.user.branch_name || 'Main Branch'
          }));
        }
        
        // Store company info if available
        if (resData.user.company_id) {
          localStorage.setItem('userCompany', JSON.stringify({
            id: resData.user.company_id,
            name: resData.user.company_name || resData.user.company
          }));
        }

        // Store payroll roles - prioritize token, then response, then empty array
        const payrollRoles = tokenPayrollData.payroll_roles.length > 0 
          ? tokenPayrollData.payroll_roles 
          : (resData.user.payroll_roles && Array.isArray(resData.user.payroll_roles) 
              ? resData.user.payroll_roles 
              : []);
        
        if (payrollRoles.length > 0) {
          localStorage.setItem('userPayrollRoles', JSON.stringify(payrollRoles));
        }

        // Store payroll permissions - prioritize token, then response, then empty array
        const payrollPermissions = tokenPayrollData.payroll_permissions.length > 0 
          ? tokenPayrollData.payroll_permissions 
          : (resData.user.payroll_permissions && Array.isArray(resData.user.payroll_permissions) 
              ? resData.user.payroll_permissions 
              : []);
        
        if (payrollPermissions.length > 0) {
          localStorage.setItem('userPayrollPermissions', JSON.stringify(payrollPermissions));
        }
      } else {
        // Fallback for old response format - try to extract role from existing data
        // This maintains backward compatibility
        const existingRole = localStorage.getItem('userRole');
        if (!existingRole) {
          // Set default role for old format
          localStorage.setItem('userRole', 'user');
        }
        
        // Still try to extract payroll data from token even if user object is missing
        if (tokenPayrollData.payroll_roles.length > 0) {
          localStorage.setItem('userPayrollRoles', JSON.stringify(tokenPayrollData.payroll_roles));
        }
        if (tokenPayrollData.payroll_permissions.length > 0) {
          localStorage.setItem('userPayrollPermissions', JSON.stringify(tokenPayrollData.payroll_permissions));
        }
      }
      
      handleSuccess(resData);
      return;
    },
    onError:(err) => {
      const errorInfo = ErrorHandler(err);
      
      // Handle rate limit errors separately - don't show alert, let component handle it
      if (errorInfo?.isRateLimited) {
        if (handleError) {
          handleError(err);
        }
        return;
      }
      
      // For non-rate-limit errors, show alert as usual
      const errorMessage = typeof errorInfo === 'string' ? errorInfo : errorInfo?.message || 'An error occurred';
      showErrorAlert(errorMessage);
      
      if (handleError) {
        handleError(err);
      }
    }
  });

  return {
    loginData: data,
    loginError: ErrorHandler(error),
    loginIsLoading: isPending,
    loginPayload: (requestPayload) => mutate(requestPayload),
  };
};


export const logOut = () => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = window.location.origin?.includes("localhost")
      ? `${window.location.origin}/login`
      : `${process.env.REACT_APP_LANDING_APP_URL}/login`;
};

export const isLoggedIn = () => {
    const token = getToken();
    return !!token && !isTokenExpired(token);
};


export const isTokenExpired = (token) => {
    try {
      const decoded = jwtDecode(token);
        if (decoded.exp < Date.now() / 1000) {
          // Checking if token is expired. N
          return true;
        } else return false;
  
    } catch (err) {
      return true;
    }
  };

  


  export const refreshToken = async () => {
    let refresh_token = sessionStorage.getItem("auth_token_refresh");
    if (isLoggedIn()) {
      try {
        const result = await httpService.postDataWithoutToken(
          {},
          routes.ClientValidation(refresh_token),
        );
        const newToken = result?.data?.access_token;
        const newRefreshToken = result?.data?.refresh_token; // Get new refresh token from response
        
        // Update access token
        sessionStorage.setItem("auth_token", stringifyData(newToken));
        
        // Update refresh token if provided (token rotation)
        if (newRefreshToken) {
          sessionStorage.setItem("auth_token_refresh", newRefreshToken);
        }
        
        // Extract and update payroll roles/permissions from new token
        if (newToken) {
          const tokenPayrollData = extractPayrollDataFromToken(newToken);
          
          // Update localStorage with fresh data from token
          if (tokenPayrollData.payroll_roles.length > 0) {
            localStorage.setItem('userPayrollRoles', JSON.stringify(tokenPayrollData.payroll_roles));
          }
          if (tokenPayrollData.payroll_permissions.length > 0) {
            localStorage.setItem('userPayrollPermissions', JSON.stringify(tokenPayrollData.payroll_permissions));
          }
        }
        
        return { success: true, access_token: newToken, refresh_token: newRefreshToken };
      } catch (err) {
        console.error('Error refreshing token:', err);
        throw err; // Re-throw to allow caller to handle error
      }
    }
    throw new Error('User is not logged in');
  };


  export const useChangePassword = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) =>
      httpService.postData(payload, routes.changePassword()),
    onSuccess: (requestParams) => {
      const resData = requestParams || {};
      sessionStorage.setItem(
        "firstTimeLogin", false
      );
      showSuccessAlert("Password changed successfully");
      handleSuccess(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    changePwrdData: data,
    changePwrdError: ErrorHandler(error),
    changePwrdIsLoading: isPending,
    changePwrdPayload: (requestPayload) => mutate(requestPayload),
  };
};

// OTP-based password reset flow
export const useSendOTP = (handleSuccess, handleError) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) =>
      httpService.postDataWithoutToken(payload, routes.sendOTP()),
    onSuccess: (requestParams) => {
      const resData = requestParams || {};
      showSuccessAlert("OTP sent successfully to your email.");
      handleSuccess(resData);
    },
    onError: (err) => {
      const errorInfo = ErrorHandler(err);
      
      // Handle rate limit errors separately - don't show alert, let component handle it
      if (errorInfo?.isRateLimited) {
        if (handleError) {
          handleError(err);
        }
        return;
      }
      
      // For non-rate-limit errors, show alert as usual
      const errorMessage = typeof errorInfo === 'string' ? errorInfo : errorInfo?.message || 'An error occurred';
      showErrorAlert(errorMessage);
      
      if (handleError) {
        handleError(err);
      }
    },
  });

  return {
    sendOTPData: data,
    sendOTPError: ErrorHandler(error),
    sendOTPIsLoading: isPending,
    sendOTPPayload: (requestPayload) => mutate(requestPayload),
  };
};

export const useVerifyOTP = (handleSuccess, handleError) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) =>
      httpService.postDataWithoutToken(payload, routes.verifyOTP()),
    onSuccess: (requestParams) => {
      const resData = requestParams || {};
      showSuccessAlert("OTP verified successfully.");
      handleSuccess(resData);
    },
    onError: (err) => {
      const errorInfo = ErrorHandler(err);
      
      // Handle rate limit errors separately - don't show alert, let component handle it
      if (errorInfo?.isRateLimited) {
        if (handleError) {
          handleError(err);
        }
        return;
      }
      
      // For non-rate-limit errors, show alert as usual
      const errorMessage = typeof errorInfo === 'string' ? errorInfo : errorInfo?.message || 'An error occurred';
      showErrorAlert(errorMessage);
      
      if (handleError) {
        handleError(err);
      }
    },
  });

  return {
    verifyOTPData: data,
    verifyOTPError: ErrorHandler(error),
    verifyOTPIsLoading: isPending,
    verifyOTPPayload: (requestPayload) => mutate(requestPayload),
  };
};

export const useResendOTP = (handleSuccess, handleError) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) =>
      httpService.postDataWithoutToken(payload, routes.resendOTP()),
    onSuccess: (requestParams) => {
      const resData = requestParams || {};
      showSuccessAlert("New OTP sent successfully. Please check your email.");
      handleSuccess(resData);
    },
    onError: (err) => {
      const errorInfo = ErrorHandler(err);
      
      // Handle rate limit errors separately - don't show alert, let component handle it
      if (errorInfo?.isRateLimited) {
        if (handleError) {
          handleError(err);
        }
        return;
      }
      
      // For non-rate-limit errors, show alert as usual
      const errorMessage = typeof errorInfo === 'string' ? errorInfo : errorInfo?.message || 'An error occurred';
      showErrorAlert(errorMessage);
      
      if (handleError) {
        handleError(err);
      }
    },
  });

  return {
    resendOTPData: data,
    resendOTPError: ErrorHandler(error),
    resendOTPIsLoading: isPending,
    resendOTPPayload: (requestPayload) => mutate(requestPayload),
  };
};

export const useSetNewPassword = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) =>
      httpService.postDataWithoutToken(payload, routes.setNewPassword()),
    onSuccess: (requestParams) => {
      const resData = requestParams || {};
      showSuccessAlert("Password reset successfully. You can now log in with your new password.");
      handleSuccess(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    setNewPasswordData: data,
    setNewPasswordError: ErrorHandler(error),
    setNewPasswordIsLoading: isPending,
    setNewPasswordPayload: (requestPayload) => mutate(requestPayload),
  };
};

  
  
  

